"use client";

// libraries/components/src/components/upcoming-trips/features/build-flight-cards.tsx

import type { ReactElement, ComponentType } from "react";
import { Card } from "@jetblue/kit/card";
import type {
  Amenity,
  ItineraryContent,
  ItineraryState,
  PnrItinAirSegState,
} from "@jb-manage-trips/types/itinerary";

import { IcPlaneFlying } from "@jetblue/kit-icons/ic-plane-flying";
import { IcBaggage } from "@jetblue/kit-icons/ic-baggage";
import { IcMintIndicator } from "@jetblue/kit-icons/ic-mint-indicator";
import { IcBed } from "@jetblue/kit-icons/ic-bed";
import { IcCheckCircle } from "@jetblue/kit-icons/ic-check-circle";
import { IcTravelingTogether } from "@jetblue/kit-icons/ic-traveling-together";
import { IcHandshake } from "@jetblue/kit-icons/ic-handshake";
import { IcGlobe } from "@jetblue/kit-icons/ic-globe";

export type BuildFlightCardsOptions = {
  itinerary: ItineraryState[];
  content: ItineraryContent;
  layoverThresholdMinutes?: number;
};
export type BuiltFlightCard = {
  key: string;
  tripKey: string;
  tripIndex: number;
  legIndex: number;
  legCount: number;
  imageUrl: string;
  destinationLabel: string;
  tripMetaLabel: string;
  confirmationCode: string;
  statusLabel: string;
  flightDetailCard: ReactElement;
};

const getUserLocale = (): string =>
  (typeof navigator !== "undefined" && navigator.language) || "en-US";

const DEFAULT_DATE_LABEL_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
};

type IconComponentProperties = {
  className?: string;
  color?: string;
};

function formatDateLabel(
  iso: string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) {
    return "";
  }

  return value.toLocaleDateString(
    locale,
    options ?? DEFAULT_DATE_LABEL_OPTIONS,
  );
}

function formatTimeLabel(iso: string | undefined, locale: string): string {
  if (!iso) {
    return "";
  }

  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) {
    return "";
  }

  return value.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Prefer backend-provided minutes */
function formatDurationFromMinutes(totalMinutes: number | undefined): string {
  if (totalMinutes == null || Number.isNaN(totalMinutes)) {
    return "";
  }

  const minutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  return `${String(hours)}h ${remainder.toString().padStart(2, "0")}m`;
}

function getCityLabel(value: string | undefined): string {
  if (!value) {
    return "";
  }

  return value.replaceAll(/\s*\([^)]+\)\s*/g, "").trim();
}

function formatTripDateRange(
  firstIso: string | undefined,
  lastIso: string | undefined,
  locale: string,
): string {
  if (!firstIso) {
    return "";
  }

  const start = formatDateLabel(firstIso, locale);
  const end = lastIso ? formatDateLabel(lastIso, locale) : "";

  if (!end || start === end) {
    return start;
  }

  return `${start} - ${end}`;
}

function getGapInMinutes(
  earlierIso: string | undefined,
  laterIso: string | undefined,
): number | null {
  if (!earlierIso || !laterIso) {
    return null;
  }

  const earlier = new Date(earlierIso);
  const later = new Date(laterIso);

  if (Number.isNaN(earlier.getTime()) || Number.isNaN(later.getTime())) {
    return null;
  }

  return Math.round((later.getTime() - earlier.getTime()) / 60_000);
}

function getShellDestinationIndex(
  segments: PnrItinAirSegState[],
  layoverThresholdMinutes = 24 * 60,
): number {
  if (segments.length === 0) {
    return -1;
  }

  for (let index = 0; index < segments.length - 1; index += 1) {
    const currentSegment = segments[index];
    const nextSegment = segments[index + 1];

    const gapInMinutes = getGapInMinutes(
      currentSegment?.scheduledArrivalTime,
      nextSegment?.scheduledDepartureTime,
    );

    if (gapInMinutes != null && gapInMinutes >= layoverThresholdMinutes) {
      return index;
    }
  }

  return segments.length - 1;
}

function getShellDestinationLabel(
  segments: PnrItinAirSegState[],
  layoverThresholdMinutes = 24 * 60,
): string {
  if (segments.length === 0) {
    return "";
  }

  const shellDestinationIndex = getShellDestinationIndex(
    segments,
    layoverThresholdMinutes,
  );

  if (shellDestinationIndex < 0) {
    return "";
  }

  const shellDestination = getCityLabel(
    segments[shellDestinationIndex]?.destination,
  );

  const priorStops = Math.max(0, shellDestinationIndex);

  if (priorStops <= 0) {
    return shellDestination;
  }

  return `${shellDestination} + ${String(priorStops)}`;
}

function CopyIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      className="size-6 text-royal-blue"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        width="10"
        x="9"
        y="7"
      />
      <path
        d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

const iconMap: Record<string, ComponentType<IconComponentProperties>> = {
  "ic-mint-indicator": IcMintIndicator,
  "ic-plane-flying": IcPlaneFlying,
  "ic-baggage": IcBaggage,
  "ic-bed": IcBed,
  "ic-checked-circle": IcCheckCircle,
  "ic-traveling-together": IcTravelingTogether,
  "ic-handshake": IcHandshake,
  "ic-globe": IcGlobe,
};

type BookerTabIcon = keyof typeof iconMap;

const Icon = ({
  icon,
  color,
  className,
}: {
  icon: BookerTabIcon;
  color?: string;
  className?: string;
}): ReactElement => {
  const Component = iconMap[icon];

  if (!Component) {
    return <span className={className} />;
  }

  return <Component className={className} color={color} />;
};

function getAmenitiesForClassOfService(
  content: ItineraryContent,
  classOfService?: string,
): Amenity[] {
  const className = classOfService?.trim();
  const configuredAmenities = className
    ? content.amenities[className]
    : undefined;
  const resolvedAmenities = configuredAmenities ?? content.amenities.Main;

  if (!resolvedAmenities) {
    return [];
  }

  return Array.isArray(resolvedAmenities)
    ? resolvedAmenities
    : [resolvedAmenities];
}

function buildSeatBadges(seatNumber: string | undefined): string[] {
  if (!seatNumber) {
    return [];
  }

  return [seatNumber];
}

function buildFlightDetailCard(
  segment: PnrItinAirSegState,
  legIndex: number,
  legCount: number,
  content: ItineraryContent,
  locale: string,
): ReactElement {
  const originLabel = getCityLabel(segment.origin);
  const destinationLabel = getCityLabel(segment.destination);
  const departureDayLabel = formatDateLabel(
    segment.scheduledDepartureTime,
    locale,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
    },
  );
  const departureTimeLabel = formatTimeLabel(
    segment.scheduledDepartureTime,
    locale,
  );
  const arrivalTimeLabel = formatTimeLabel(
    segment.scheduledArrivalTime,
    locale,
  );
  const durationLabel = formatDurationFromMinutes(segment.durationInMinutes);
  const seatBadges = buildSeatBadges(segment.seatNumber);
  const amenities = getAmenitiesForClassOfService(
    content,
    segment.classOfService,
  );
  const primaryAmenity = amenities[0];

  let cabinLabel = "Main Cabin";
  if (segment.classOfService) {
    cabinLabel = `${segment.classOfService} Cabin`;
  } else if (segment.cabinClass) {
    cabinLabel = `${segment.cabinClass} Cabin`;
  }

  const flightIdLabel = `${segment.operatingAirlineCode} ${String(segment.flightNumber)}`;

  return (
    <Card
      as="div"
      className="w-full rounded-2xl border border-medium-gray bg-white p-4 shadow-none md:p-5 lg:min-h-[440px] lg:p-0"
    >
      <div className="rounded-t-2xl bg-off-white px-4 py-2 text-xs-heading-extra-bold text-charcoal md:px-5">
        {`Flight ${String(legIndex + 1)} of ${String(legCount)}`}
      </div>

      <div className="space-y-5 px-4 py-2 text-core-blue md:p-5 lg:space-y-6 lg:px-5 lg:py-4">
        <div className="flex items-start justify-between gap-4 text-s-body md:text-body">
          <div className="text-charcoal/80">{departureDayLabel}</div>
          <div className="text-charcoal/80">{flightIdLabel}</div>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_96px_minmax(0,1fr)] items-start gap-2 md:grid-cols-[minmax(0,1fr)_minmax(240px,360px)_minmax(0,1fr)] md:gap-4">
          <div className="min-w-0">
            <div
              className="truncate text-s-body whitespace-nowrap text-charcoal md:text-body"
              title={originLabel}
            >
              {originLabel}
            </div>
            <div className="mt-1 text-l-heading leading-none text-core-blue md:text-l-heading">
              {segment.originCode}
            </div>
            <div className="mt-1 text-s-heading-extra-bold text-core-blue md:text-s-heading-extra-bold">
              {departureTimeLabel}
            </div>
          </div>

          <div className="flex min-w-0 flex-col items-center justify-start pt-5 text-slate-gray md:pt-6">
            <div className="flex w-full items-center gap-3 md:gap-4">
              <span className="block h-0 w-full border-t-2 border-dashed border-dark-gray/80" />
              <Icon
                className="size-8 shrink-0 text-core-blue md:size-10"
                icon="ic-plane-flying"
              />
              <span className="block h-0 w-full border-t-2 border-dashed border-dark-gray/80" />
            </div>
            <div className="mt-4 text-s-body md:text-body">{durationLabel}</div>
          </div>

          <div className="min-w-0 text-right">
            <div
              className="truncate text-s-body whitespace-nowrap text-charcoal md:text-body"
              title={destinationLabel}
            >
              {destinationLabel}
            </div>
            <div className="mt-1 text-l-heading leading-none text-core-blue md:text-l-heading">
              {segment.destinationCode}
            </div>
            <div className="mt-1 text-s-heading-extra-bold text-core-blue md:text-s-heading-extra-bold">
              {arrivalTimeLabel}
            </div>
          </div>
        </div>

        <div className="border-t border-medium-gray pt-5">
          <div className="grid grid-cols-[56px_minmax(0,1fr)] items-start gap-4 md:grid-cols-[64px_minmax(0,1fr)] md:gap-x-5">
            <div className="flex justify-center pt-1">
              <Icon
                className="size-16 shrink-0 text-core-blue"
                icon="ic-globe"
              />
            </div>

            <div className="min-w-0">
              <div className="text-xs-heading text-core-blue md:text-s-heading">
                {cabinLabel}
              </div>

              {seatBadges.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {seatBadges.map((seat, seatIndex) => (
                    <span
                      className="rounded-md border border-medium-gray bg-white px-2 py-1 text-s-body-extra-bold text-core-blue"
                      key={`${seat}-${String(seatIndex)}`}
                    >
                      {seat}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {primaryAmenity && (
              <>
                <div className="flex justify-center pt-1">
                  <div className="flex size-16 items-center justify-center">
                    <Icon
                      className="size-16 text-core-blue"
                      icon={primaryAmenity.icon}
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <div className="text-xs-heading text-core-blue md:text-s-heading">
                    {primaryAmenity.label}
                  </div>
                  <div className="text-s-body text-slate-gray">
                    {primaryAmenity.sublabel}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-medium-gray pt-5">
          <div className="grid grid-cols-[56px_minmax(0,1fr)] items-start gap-x-4 md:grid-cols-[64px_minmax(0,1fr)] md:gap-x-5">
            <div className="flex items-center justify-center pt-1">
              <span className="inline-block font-bold text-core-blue">
                jetBlue
              </span>
            </div>

            <div className="min-w-0 text-s-body text-slate-gray md:text-s-body">
              <div>{`Operated by ${segment.operatingAirlineShortName}`}</div>
              <div>{`${segment.equipmentType} ${segment.classOfService}`}</div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function buildFlightCards({
  itinerary,
  content,
  layoverThresholdMinutes = 24 * 60,
}: BuildFlightCardsOptions): BuiltFlightCard[] {
  if (!Array.isArray(itinerary) || itinerary.length === 0) {
    return [];
  }

  const locale = getUserLocale();

  return itinerary.flatMap((itineraryItem, tripIndex) => {
    const segments = itineraryItem.pnrItinAirSeg;

    if (segments.length === 0) {
      return [];
    }

    const confirmationCode =
      itineraryItem.b6PNRRecordLocator ?? itineraryItem.pnrRecordLocator ?? "";

    const tripKey = confirmationCode || `trip-${String(tripIndex)}`;
    const shellDestinationLabel = getShellDestinationLabel(
      segments,
      layoverThresholdMinutes,
    );
    const firstSegment = segments[0];
    const lastSegment = segments.at(-1);

    if (!firstSegment || !lastSegment) {
      return [];
    }

    const tripTypeLabel = segments.length > 1 ? "Multi-city" : "One-way";
    const tripDateRangeLabel = formatTripDateRange(
      firstSegment.scheduledDepartureTime,
      lastSegment.scheduledArrivalTime,
      locale,
    );

    let tripMetaLabel = tripTypeLabel;
    if (tripDateRangeLabel) {
      tripMetaLabel = `${tripDateRangeLabel} • ${tripTypeLabel}`;
    }

    return segments.map((segment, legIndex) => {
      const cardKey = `${tripKey}-leg-${String(legIndex)}`;

      return {
        key: cardKey,
        tripKey,
        tripIndex,
        legIndex,
        legCount: segments.length,
        imageUrl: itineraryItem.tripImage,
        destinationLabel: shellDestinationLabel,
        tripMetaLabel,
        confirmationCode,
        statusLabel: "Check-in is open",
        flightDetailCard: buildFlightDetailCard(
          segment,
          legIndex,
          segments.length,
          content,
          locale,
        ),
      };
    });
  });
}
export { CopyIcon };
